/**
 * Presentación de los rangos/recompensas de FamiDex: traducción i18n por `id`
 * estable (el dominio no traduce) e iconos lucide (el diseño Stitch usa Material
 * Symbols; los mapeamos al set lucide de la app). Sin lógica de negocio.
 */
import { msg } from "@lingui/core/macro";
import type { Component, ComponentProps } from "solid-js";
import {
  Award,
  BookOpen,
  Frame,
  Home,
  Landmark,
  Lightbulb,
  ShoppingBasket,
  Snowflake,
  Sparkles,
  Tag,
} from "lucide-solid";
import type { useI18n } from "@/providers/i18n-provider";

type Translate = ReturnType<typeof useI18n>["t"];

/** Nombre humano de un rango por su id estable (i18n reactivo). */
export function rankName(t: Translate, id: string): string {
  switch (id) {
    case "novato": {
      return t(msg`Recolector Novato`);
    }
    case "guia_bosque": {
      return t(msg`Guía del Bosque`);
    }
    case "buscador_estancias": {
      return t(msg`Buscador de Estancias`);
    }
    case "catalogador_maestro": {
      return t(msg`Catalogador Maestro`);
    }
    case "gran_conservador": {
      return t(msg`Gran Conservador`);
    }
    case "venerable_anciano": {
      return t(msg`Venerable Anciano`);
    }
    default: {
      return id;
    }
  }
}

/** Lema/descripción de un rango por su id. */
export function rankTagline(t: Translate, id: string): string {
  switch (id) {
    case "novato": {
      return t(msg`El comienzo de una gran aventura familiar.`);
    }
    case "guia_bosque": {
      return t(msg`Conoces cada sendero y cada rincón oculto.`);
    }
    case "buscador_estancias": {
      return t(msg`Experto en encontrar los hogares más acogedores.`);
    }
    case "catalogador_maestro": {
      return t(msg`Tu colección es un tesoro para toda la glade.`);
    }
    case "gran_conservador": {
      return t(msg`Custodio de la historia y el legado de Sylvanian.`);
    }
    case "venerable_anciano": {
      return t(msg`La máxima distinción para el coleccionista sabio.`);
    }
    default: {
      return "";
    }
  }
}

/** Texto de una recompensa por su clave. */
export function rewardLabel(t: Translate, key: string): string {
  switch (key) {
    case "custom_badge": {
      return t(msg`Insignia personalizada`);
    }
    case "bronze_border": {
      return t(msg`Marco de perfil de bronce`);
    }
    case "shop_discount_15": {
      return t(msg`15% de descuento en la Tienda del Bosque`);
    }
    case "oak_frame": {
      return t(msg`Marco de Roble Envejecido`);
    }
    case "exclusive_stickers": {
      return t(msg`Stickers exclusivos`);
    }
    case "golden_glow": {
      return t(msg`Brillo dorado en el perfil`);
    }
    default: {
      return key;
    }
  }
}

/** Componente de icono lucide para un rango (por su `icon` de dominio). */
export function rankIcon(icon: string): Component<ComponentProps<"svg">> {
  switch (icon) {
    case "shopping_basket": {
      return ShoppingBasket;
    }
    case "emoji_objects": {
      return Lightbulb;
    }
    case "home": {
      return Home;
    }
    case "auto_stories": {
      return BookOpen;
    }
    case "museum": {
      return Landmark;
    }
    case "ac_unit": {
      return Snowflake;
    }
    default: {
      return Award;
    }
  }
}

/** Componente de icono lucide para una recompensa (por su `icon` de dominio). */
export function rewardIcon(icon: string): Component<ComponentProps<"svg">> {
  switch (icon) {
    case "military_tech": {
      return Award;
    }
    case "frame_person": {
      return Frame;
    }
    case "sell": {
      return Tag;
    }
    case "auto_awesome": {
      return Sparkles;
    }
    default: {
      return Award;
    }
  }
}
